import * as Sentry from '@sentry/browser';
import { isObject } from 'lodash';
import PapaParse from 'papaparse';
import DOMPurify from 'dompurify';
import moment from 'moment';

let isLoggerInitialized = false;
const MAX_NUMBER_OF_BREADCRUMBS = 20;
const Logger = {
    init({ dsn, environment, release }) {
        if (dsn) {
            Sentry.init({
                dsn,
                environment,
                release,
                maxBreadcrumbs: MAX_NUMBER_OF_BREADCRUMBS,
            });
            isLoggerInitialized = true;
        }
    },
    reset() {
        isLoggerInitialized = false;
        Logger.withScopeCallback = null;
    },
    configureScope(callback) {
        if (isLoggerInitialized) {
            Sentry.configureScope(callback);
        }
    },
    logError(message, { payload, error } = {}) {
        if (error) {
            let combinedErrorMessage = message;
            if (error instanceof Error && error.message) {
                combinedErrorMessage += `:\n  ${error.message}`;
            }
            else if (typeof error === "string") {
                combinedErrorMessage += `:\n  ${error}`;
            }
            else {
                Logger.logError("Logger.logError: has been called with an unsupported error type", { payload: { error } });
            }
            if (error instanceof Error) {
                const cloned = new Error(combinedErrorMessage);
                cloned.name = error.name;
                cloned.stack = error.stack;
                error = cloned;
            }
            else {
                payload = {
                    originalError: error,
                    payload,
                };
                error = new Error(combinedErrorMessage);
            }
        }
        else {
            error = new Error(message);
        }
        if (isLoggerInitialized) {
            Logger.withScopeCallback = (scope) => {
                if (isObject(payload)) {
                    scope.setExtras(payload);
                }
                Sentry.captureException(error);
                console.error(error);
            };
            Sentry.withScope(Logger.withScopeCallback);
        }
        else {
            console.error(error);
        }
    },
    logWarning(message) {
        if (isLoggerInitialized) {
            Sentry.captureMessage(message, "warning");
        }
        else {
            console.warn(message);
        }
    },
    logDebug(message) {
        if (isLoggerInitialized) {
            Sentry.captureMessage(message, "debug");
        }
        else {
            console.debug(message);
        }
    },
    withScopeCallback: null,
};

const WAIT_FOR_INIT_INTERVAL = 1000;
const WAIT_FOR_INIT_TIMEOUT = 10000;
class AmplitudeClient {
    instance;
    identify;
    userId;
    eventStack = [];
    isFlushing = 0;
    async init(callback, { shouldForceLibraryLoad } = {}) {
        if (shouldForceLibraryLoad) {
            pushToGTMDataLayer({
                event: GTM_INITIALIZE_AMPLITUDE,
            });
        }
        const instance = await this.waitForInit();
        if (instance) {
            this.instance = instance;
            callback && callback();
        }
    }
    waitForInit() {
        return new Promise((resolve, reject) => {
            const intervalId = setInterval(() => {
                if (AmplitudeClient.isInitialized()) {
                    clearInterval(intervalId);
                    resolve(AmplitudeClient.getInstance());
                }
            }, WAIT_FOR_INIT_INTERVAL);
            setTimeout(() => {
                if (!AmplitudeClient.isInitialized()) {
                    clearInterval(intervalId);
                    resolve(null);
                }
            }, WAIT_FOR_INIT_TIMEOUT);
        });
    }
    async setUserId(userId, shouldPreventTracking = false) {
        if (userId && !this.userId) {
            this.userId = userId;
            this._setUserId(userId);
            this.identify = AmplitudeClient.getIdentify();
            this.instance.identify(this.identify);
            if (!shouldPreventTracking) {
                this.logEvent("User login");
            }
        }
    }
    async setUserProperty(propertyName, propertyValue) {
        if (this.instance && this.identify) {
            this.identify.set(propertyName, propertyValue);
            this.instance.identify(this.identify);
        }
    }
    clearIdentifiableInformation() {
        this._setUserId(null);
        this.instance.clearUserProperties();
        this.userId = null;
        this.identify = AmplitudeClient.getIdentify();
    }
    async logEvent(msg, eventProps = {}, appProps = {}) {
        this.eventStack.push({ msg, eventProps, appProps });
        this.flushStack();
    }
    async logEventAsync(msg, eventProps = {}, appProps = {}) {
        const allEventProps = {
            ...appProps,
            ...eventProps,
        };
        return await this.instance.logEvent(msg, allEventProps);
    }
    logoutUser() {
        this._setUserId(null);
        this.logEvent("User logout");
        this.userId = null;
        this.eventStack = [];
    }
    _setUserId(userId) {
        this.instance.setUserId(userId);
    }
    sendEvent(event) {
        if (event) {
            const allEventProps = {
                ...event.appProps,
                ...event.eventProps,
            };
            this.instance.logEvent(event.msg, allEventProps);
        }
    }
    cancelFlush() {
        clearInterval(this.isFlushing);
        this.isFlushing = undefined;
    }
    flushStack() {
        if (!this.isFlushing) {
            this.isFlushing = window.setInterval(async () => {
                if (AmplitudeClient.isInitialized()) {
                    if (this.eventStack.length) {
                        this.sendEvent(this.eventStack.shift());
                        this.flushStack();
                    }
                    else {
                        this.cancelFlush();
                    }
                }
            }, 1000);
        }
    }
    static isInitialized() {
        return (!!window.amplitude && typeof window.amplitude.getInstance === "function");
    }
    hasInstanceLogEvent() {
        if (!this.instance || !this.instance.logEvent) {
            return false;
        }
        return typeof this.instance.logEvent === "function";
    }
    static getInstance() {
        return window.amplitude.getInstance();
    }
    static getIdentify() {
        return new window.amplitude.Identify();
    }
}
const GTM_INITIALIZE_AMPLITUDE = "GTM_INITIALIZE_AMPLITUDE";
function pushToGTMDataLayer(data) {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(data);
    }
    catch (error) {
        Logger.logError("Could not push to GTM", error);
    }
}

function asyncSynchronized(fn, timeoutDuration = -1) {
    let runningPromise;
    return (async (...args) => {
        if (runningPromise) {
            return runningPromise;
        }
        else {
            runningPromise = fn(...args);
            try {
                let timeoutID = null;
                if (timeoutDuration >= 0) {
                    timeoutID = setTimeout(() => {
                        runningPromise = undefined;
                    }, timeoutDuration);
                }
                const data = await runningPromise;
                runningPromise = undefined;
                if (timeoutID) {
                    clearTimeout(timeoutID);
                }
                return data;
            }
            catch (error) {
                runningPromise = undefined;
                throw error;
            }
        }
    });
}
async function timeoutPromise(timeout, value) {
    return new Promise((resolve) => {
        setTimeout(() => resolve(value), timeout);
    });
}

var asyncUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  asyncSynchronized: asyncSynchronized,
  timeoutPromise: timeoutPromise
});

const Analytics = {
    amplitudeClient: undefined,
    isConsentGiven: false,
    shouldPreventTracking: false,
    defaultProps: {},
    init({ props, isConsentGiven, shouldPreventTracking, } = {}) {
        Analytics.isConsentGiven = !!isConsentGiven;
        Analytics.shouldPreventTracking = !!shouldPreventTracking;
        if (!Analytics.amplitudeClient) {
            Analytics.amplitudeClient = new AmplitudeClient();
        }
        try {
            Analytics.amplitudeClient.init(() => {
                if (props) {
                    Analytics.props = props;
                }
            }, {
                shouldForceLibraryLoad: !!props?.userId,
            });
        }
        catch (error) {
            Logger.logError("Amplitude client could not be initialized", { error });
        }
    },
    track({ name, props }) {
        if (Analytics.shouldPreventTracking) {
            return;
        }
        if (Analytics.amplitudeClient) {
            Analytics.amplitudeClient.logEvent(name, {
                ...props,
                ...Analytics.defaultProps,
            });
        }
        else {
            Logger.logDebug("Can not log analytics events before analytics have been initialized");
        }
    },
    isAmplitudeReady() {
        return (AmplitudeClient.isInitialized() &&
            !!Analytics.amplitudeClient &&
            Analytics.amplitudeClient.hasInstanceLogEvent());
    },
    async waitUntilAmplitudeIsReadyIndefinitely() {
        return new Promise((resolve) => {
            function checkAmplitude() {
                if (Analytics.isAmplitudeReady()) {
                    resolve();
                    return;
                }
                setTimeout(checkAmplitude, 100);
            }
            checkAmplitude();
        });
    },
    async waitUntilAmplitudeIsReady() {
        return new Promise(async (resolve, reject) => {
            const timeoutResponse = "TIMEOUT_RESPONSE";
            const timeoutBeforeReject = 10000;
            const response = await Promise.race([
                Analytics.waitUntilAmplitudeIsReadyIndefinitely(),
                timeoutPromise(timeoutBeforeReject, timeoutResponse),
            ]);
            if (response === timeoutResponse) {
                reject("Timeout waiting for amplitude to be ready");
                return;
            }
            resolve();
        });
    },
    async trackAsync({ name, props, shouldWaitForInitialization, }) {
        if (Analytics.shouldPreventTracking) {
            return;
        }
        try {
            if (shouldWaitForInitialization) {
                await Analytics.waitUntilAmplitudeIsReady();
            }
            if (!Analytics.amplitudeClient) {
                Logger.logDebug("Amplitude client is not ready after waiting using waitUntilAmplitudeIsReady");
                return;
            }
            await Analytics.amplitudeClient.logEventAsync(name, {
                ...props,
                ...Analytics.defaultProps,
            });
        }
        catch (error) {
            Logger.logDebug("Error waiting for Amplitude to be ready");
        }
    },
    shouldUserBeExcludedFromTracking(userEmail) {
        const isInternalUser = userEmail.endsWith("faro.com") || userEmail.endsWith("holobuilder.com");
        return isInternalUser;
    },
    set props({ defaultProps: newDefaultProps, userId, userProps, }) {
        if (newDefaultProps) {
            Analytics.defaultProps = { ...newDefaultProps };
        }
        if (Analytics.amplitudeClient) {
            if (Analytics.isConsentGiven) {
                if (userId) {
                    Analytics.amplitudeClient.setUserId(userId, Analytics.shouldPreventTracking);
                }
                if (userProps) {
                    for (const key in userProps) {
                        if (Object.prototype.hasOwnProperty.call(userProps, key)) {
                            Analytics.amplitudeClient.setUserProperty(key, userProps[key]);
                        }
                    }
                }
            }
            else {
                Analytics.amplitudeClient.clearIdentifiableInformation();
            }
        }
    },
};

function isSafari() {
    const ua = navigator.userAgent;
    console.log("User Agent: ", ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isSafari && "safari" in window;
}
function redirectToUrl(url) {
    window.location.href = url;
}
function constructUrl({ url, additionalParams, }) {
    try {
        const urlObject = new URL(url);
        if (additionalParams) {
            const stringifiedParams = new URLSearchParams(additionalParams).toString();
            urlObject.search = urlObject.search
                ? `${urlObject.search}&${stringifiedParams}`
                : stringifiedParams;
        }
        return urlObject;
    }
    catch (error) {
        console.error("Invalid url format. ", error);
        return null;
    }
}
function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    }
    catch (error) {
        return false;
    }
}
function readSearchParam(paramKey) {
    try {
        const appUrl = new URL(window.location.href);
        const params = appUrl.searchParams;
        return params.get(paramKey);
    }
    catch (error) {
        return null;
    }
}

var browserUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  constructUrl: constructUrl,
  isSafari: isSafari,
  isValidUrl: isValidUrl,
  readSearchParam: readSearchParam,
  redirectToUrl: redirectToUrl
});

const jsUrl = /javascript:/;
function sanitizeString(str, options = { sanitizeFn: DOMPurify.sanitize }) {
    if (str !== undefined && str !== null && isFinite(str)) {
        return str;
    }
    if (!options) {
        throw new Error("sanitizeString: options are invalid");
    }
    if (!options.sanitizeFn) {
        throw new Error("sanitizeString: options.sanitizeFn is invalid");
    }
    const string = String(str).replace(jsUrl, "");
    return options.sanitizeFn(string);
}
function sanitizeFilename(filename) {
    return filename.replace(/[/?<>\\:*|"]/g, "_").trim();
}
function sanitizeData(value) {
    if (typeof value === "string") {
        return value.replace(/(^|[;,])([\s=+\-@]+)/g, "$1");
    }
    return value;
}
function sanitizeDataForCSV(data) {
    if (!Array.isArray(data) || data.length === 0) {
        return data;
    }
    if (isArrayOfObjects(data)) {
        return data.map((dataItem) => sanitizeObject(dataItem));
    }
    if (isArrayOfArrays(data)) {
        return data.map((dataItem) => sanitizeArray(dataItem));
    }
    return data;
}
function isArrayOfObjects(data) {
    return data.every((item) => typeof item === "object" && item !== null && !Array.isArray(item));
}
function isArrayOfArrays(data) {
    return data.every((item) => Array.isArray(item));
}
function sanitizeArray(dataItem) {
    return dataItem.map((cell) => typeof cell === "string" || typeof cell === "number"
        ? sanitizeData(cell)
        : cell);
}
function sanitizeObject(dataItem) {
    return Object.fromEntries(Object.entries(dataItem).map(([key, value]) => [
        key,
        typeof value === "string" || typeof value === "number"
            ? sanitizeData(value)
            : value,
    ]));
}

var sanitizeUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  sanitizeData: sanitizeData,
  sanitizeDataForCSV: sanitizeDataForCSV,
  sanitizeFilename: sanitizeFilename,
  sanitizeString: sanitizeString
});

function downloadFilePromise(url, { onFileDownloadStart, onFileDownloadProgress, onFileDownloadEnd, } = {}) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.responseType = "blob";
        if (onFileDownloadProgress) {
            xhr.onprogress = onFileDownloadProgress;
        }
        xhr.onload = function (ev) {
            if (this.status === 200) {
                resolve(this.response);
            }
            else {
                reject(new Error(`downloadFilePromise() failed with status code ${this.status}: ${this.response}`));
            }
            if (onFileDownloadEnd) {
                onFileDownloadEnd();
            }
        };
        xhr.onerror = (ev) => {
            if (onFileDownloadEnd) {
                onFileDownloadEnd();
            }
            reject(ev);
        };
        if (onFileDownloadStart) {
            onFileDownloadStart();
        }
        xhr.send();
    });
}
function downloadFile(fileName, fileUrl) {
    if (!isSafari()) {
        const link = document.createElement("a");
        link.style.display = "none";
        link.download = fileName;
        link.href = fileUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    else {
        const iframe = document.createElement("iframe");
        iframe.src = fileUrl;
        iframe.style.visibility = "hidden";
        iframe.style.display = "none";
        document.body.appendChild(iframe);
        setTimeout(() => {
            iframe.remove();
        }, 1000);
    }
}
function downloadFileFromString(filename, text) {
    filename = sanitizeFilename(filename);
    if (window.navigator.msSaveBlob) {
        window.navigator.msSaveBlob(new Blob([text]), filename);
    }
    else {
        downloadFile(filename, `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
    }
}

var downloadUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  downloadFile: downloadFile,
  downloadFileFromString: downloadFileFromString,
  downloadFilePromise: downloadFilePromise
});

const CSVExporter = {
    toFileDownload(data, filename, config) {
        const sanitizedData = sanitizeDataForCSV(data);
        const csvString = PapaParse.unparse(sanitizedData, config);
        downloadFileFromString(filename, csvString);
    },
};

var fileDownloadCallbacks = /*#__PURE__*/Object.freeze({
  __proto__: null
});

const METER_TO_FEET_FACTOR = 3.280839895;
const SQ_METER_TO_SQ_FOOT_FACTOR = 10.7639104167;
const METER_TO_CM_FACTOR = 100;
const METER_TO_INCH_FACTOR = 39.37;
const FOOT_TO_INCH_FACTOR = 12;
const STRAIGHT_ANGLE = 180;
const FULL_CIRCLE = 360;
const isHexColor = /^#(([0-9a-fA-F]{2}){3}|([0-9a-fA-F]){3})$/;
const Convert = {
    sqMeterToSqFoot(sqMeter) {
        return SQ_METER_TO_SQ_FOOT_FACTOR * sqMeter;
    },
    sqMeterToSqFootRounded(sqMeter) {
        return Math.round(Convert.sqMeterToSqFoot(sqMeter));
    },
    meterToFeet(meter) {
        return meter * METER_TO_FEET_FACTOR;
    },
    feetToMeter(feet) {
        return feet / METER_TO_FEET_FACTOR;
    },
    meterToCm(distance) {
        return distance * METER_TO_CM_FACTOR;
    },
    cmToMeter(distance) {
        return distance / METER_TO_CM_FACTOR;
    },
    meterToInch(distance) {
        return distance * METER_TO_INCH_FACTOR;
    },
    inchToMeter(distance) {
        return distance / METER_TO_INCH_FACTOR;
    },
    footToInch(distance) {
        return distance * FOOT_TO_INCH_FACTOR;
    },
    inchToFoot(distance) {
        return distance / FOOT_TO_INCH_FACTOR;
    },
    radToDeg(radians) {
        return radians * (STRAIGHT_ANGLE / Math.PI);
    },
    degToRad(degrees) {
        return degrees * (Math.PI / STRAIGHT_ANGLE);
    },
    degToCompressed36(num) {
        num = num % FULL_CIRCLE;
        if (num < 0) {
            num += FULL_CIRCLE;
        }
        const integer = Math.floor(num);
        return Math.ceil(integer) < 36
            ? `0${Math.ceil(integer).toString(36)}`
            : Math.ceil(integer).toString(36);
    },
    compressed36DegToRad(comp36) {
        return Convert.degToRad(parseInt(comp36, 36));
    },
    floatToComposed36(num) {
        num = num % FULL_CIRCLE;
        if (num < 0) {
            num += FULL_CIRCLE;
        }
        const integer = Math.floor(num);
        const decDigits = Math.ceil((Convert.toFixedFloat(num, 3) % 1) * 1000);
        const intString = Math.ceil(integer) < 36
            ? `0${Math.ceil(integer).toString(36)}`
            : Math.ceil(integer).toString(36);
        const decString = Math.ceil(decDigits) < 36
            ? `0${Math.ceil(decDigits).toString(36)}`
            : Math.ceil(decDigits).toString(36);
        return intString + decString;
    },
    toFixedFloat(num, precision = 2) {
        return num ? parseFloat(parseFloat(num).toFixed(precision)) : 0;
    },
    sToMs(seconds) {
        return seconds * 1000;
    },
    msToS(milliseconds) {
        return milliseconds / 1000;
    },
    hexToRgba(hex, { opacity = 1 } = {}) {
        if (isHexColor.test(hex)) {
            hex = hex.toString().replace("#", "");
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return `rgba(${r},${g},${b},${opacity})`;
        }
        else {
            return "rgba(0,0,0,1)";
        }
    },
    rgbToHex(rgb) {
        const rgbMatches = rgb.match(/^rgb\(\s*(-?\d+),\s*(-?\d+),\s*(-?\d+)\)$/i);
        if (!rgbMatches) {
            return "#000000";
        }
        return rgbMatches
            .slice(1, 4)
            .reduce((rgb, part) => (rgb += decToHexInColorRange(part)), "#");
    },
};
function decToHexInColorRange(decimal) {
    const decDigit = Number(decimal);
    if (decDigit < 0 || decDigit > 255) {
        return "00";
    }
    let hex = decDigit.toString(16);
    if (hex.length < 2) {
        hex = `0${hex}`;
    }
    return hex;
}

const DataFormatter = {
    numberThousandsSeparated(x, separator = " ") {
        if (x === null) {
            return "";
        }
        else if (!isFinite(+x) || Math.abs(+x) < 10000) {
            return x.toString();
        }
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    },
};

function convertErrorToErrorWithDeveloperMessage(error, userMessage) {
    error.message = `${userMessage}<br><br>Developer Error Message:<div class="developer-error-message">${error.message}</div>`;
    return error;
}

var errorUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  convertErrorToErrorWithDeveloperMessage: convertErrorToErrorWithDeveloperMessage
});

const RepairUrl = {
    fixUploadUrl(url, params = {}) {
        url = fixLegacyUploadUrl(url, params);
        url = fixUrlCompatibility(url, params);
        return url;
    },
};
function fixLegacyUploadUrl(url, { legacyUploadBaseUrl }) {
    if (!url) {
        return url;
    }
    if (url === "https://files-legacy.holobuilder.com/undefined") {
        Logger.logDebug("Fixed invalid files-legacy URL in project (see ARWEB-16690)");
        return undefined;
    }
    try {
        return new URL(url, legacyUploadBaseUrl).toString();
    }
    catch (error) {
        return url;
    }
}
function fixUrlCompatibility(url, { isEU }) {
    if (!url) {
        return url;
    }
    if (url.startsWith("https://storage.googleapis.com/holo-files/")) {
        return url.replace("https://storage.googleapis.com/holo-files/", "https://files.holobuilder.com/");
    }
    if (url.startsWith("https://storage.googleapis.com/holo-files-staging/")) {
        return url.replace("https://storage.googleapis.com/holo-files-staging/", "https://files.staging.holobuilder.com/");
    }
    if (url.startsWith("https://holobuildereustaging.blob.core.cloudapi.de/uploads")) {
        return url.replace("https://holobuildereustaging.blob.core.cloudapi.de/uploads", "https://holobuildereu.blob.core.cloudapi.de/uploads-staging");
    }
    if (url.startsWith("https://holobuildereu.blob.core.cloudapi.de/uploads")) {
        return url.replace("https://holobuildereu.blob.core.cloudapi.de/uploads", "https://files.holobuilder.eu/uploads-germany");
    }
    if (isEU && url.startsWith("/v1/file/serve?blob-key=")) {
        return url.replace("/v1/file/serve?blob-key=", "https://files.holobuilder.eu/uploads-germany/");
    }
    return url;
}

function isProductionEnvironment(environment) {
    return environment === "com-production" || environment === "eu-production";
}

var runtimeUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  isProductionEnvironment: isProductionEnvironment
});

const AsyncFunctionReturnType = Promise;

var types = /*#__PURE__*/Object.freeze({
  __proto__: null,
  AsyncFunctionReturnType: AsyncFunctionReturnType
});

const Localization = {
    blockedClasses: ["no-localize", "material-icons", "developer-error-message"],
    isInitialized: undefined,
    get selectedLanguage() {
        return (Localize && Localize.getLanguage()) || "";
    },
    async init(key, options) {
        while (!Localize) {
            await timeoutPromise(1000);
        }
        Localization.isInitialized = new Promise((resolve, reject) => Localize.on("initialize", (localizeData) => {
            if (localizeData &&
                localizeData.key &&
                localizeData.key !== "") {
                resolve();
            }
            else {
                reject();
            }
        }));
        const additionalBlockedClasses = options
            ?
                options.blockedClasses
            : [];
        const allBlockedClasses = [
            ...this.blockedClasses,
            ...additionalBlockedClasses,
        ];
        const additionalBlockedIds = options?.blockedIds ?? [];
        const optionsLocalize = {
            key,
            rememberLanguage: true,
            autoApprove: false,
            translateMetaTags: false,
            translateTitle: false,
            retranslateOnNewPhrases: true,
            blockedClasses: allBlockedClasses,
            blockedIds: additionalBlockedIds,
            fetchTranslations: options?.fetchTranslations || true,
            translateAlt: options?.translateAlt || true,
            translateAriaLabels: options?.translateAriaLabels || true,
            prefetch: options?.prefetch || false,
            disableWidget: options?.disableWidget || false,
            localizeImages: options?.localizeImages || true,
            translateClasses: options?.translateClasses || [],
            autodetectLanguage: options?.autodetectLanguage || true,
            translateBody: options?.translateBody || true,
            defaultLanguage: options?.defaultLanguage || "en",
            basePath: options?.basePath || "",
            translateSVGElement: options?.translateSVGElement || true,
            translateTimeElement: options?.translateTimeElement || true,
            translateNumbers: options?.translateNumbers || true,
            saveNewPhrases: options?.saveNewPhrases || true,
            saveNewPhrasesFromSource: options?.saveNewPhrasesFromSource || false,
            enhancedContentSecurity: options?.enhancedContentSecurity || false,
            targetLanguage: options?.targetLanguage || "",
        };
        Localize.initialize(optionsLocalize);
    },
    async translate(input, variables) {
        await checkLocalizationInit();
        return new Promise((resolve) => {
            Localize.translate(input, variables, resolve);
        });
    },
    localizeNumber(number, options) {
        return new Intl.NumberFormat(Localization.selectedLanguage, options).format(number);
    },
    disableLocalization(node) {
        if (!node) {
            console.warn("disableLocalization: node is undefined");
            return;
        }
        node.classList.add("no-localize");
    },
    async setLanguage(languageCode) {
        const availableLanguages = await this.fetchAvailableLanguages();
        if (availableLanguages.some((language) => language.code === languageCode)) {
            Localize.setLanguage(languageCode);
        }
    },
    async fetchAvailableLanguages() {
        await checkLocalizationInit();
        return new Promise((resolve, reject) => {
            Localize.getAvailableLanguages((err, languages) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(languages);
            });
        });
    },
    registerLanguageChangeCallback(callback) {
        Localize.on("setLanguage", callback);
    },
    unregisterLanguageChangeCallback(callback) {
        Localize.off("setLanguage", callback);
    },
};
function checkLocalizationInit() {
    if (!Localization.isInitialized) {
        throw Error("Localization module is not initialized, yet! Call Localization.init first!");
    }
    return Localization.isInitialized;
}

var EAllowedDateFormat;
(function (EAllowedDateFormat) {
    EAllowedDateFormat["us"] = "M/D/YYYY";
    EAllowedDateFormat["eu"] = "D.M.YYYY";
    EAllowedDateFormat["iso"] = "YYYY-MM-DD";
    EAllowedDateFormat["generalWithSpace"] = "MMM D, YYYY";
    EAllowedDateFormat["general"] = "MMM D,YYYY";
    EAllowedDateFormat["generalWithSpaceAndTime"] = "MMM D, YYYY h:mm A";
})(EAllowedDateFormat || (EAllowedDateFormat = {}));
var ETimeUnit;
(function (ETimeUnit) {
    ETimeUnit["day"] = "day";
    ETimeUnit["week"] = "week";
    ETimeUnit["month"] = "month";
})(ETimeUnit || (ETimeUnit = {}));
var EPointOfTimeUnit;
(function (EPointOfTimeUnit) {
    EPointOfTimeUnit["start"] = "start";
    EPointOfTimeUnit["end"] = "end";
})(EPointOfTimeUnit || (EPointOfTimeUnit = {}));
const oneMonthAsDurationObject = moment.duration(1, "month");
const oneYearAsDurationObject = moment.duration(1, "year");
const Duration = {
    oneYear: {
        inDays: oneYearAsDurationObject.asDays(),
        inWeeks: Math.floor(oneYearAsDurationObject.asWeeks()),
        inMonths: oneYearAsDurationObject.asMonths(),
        inYears: oneYearAsDurationObject.asYears(),
    },
    oneMonth: {
        inDays: oneMonthAsDurationObject.asDays(),
    },
};
function isValidDate(date) {
    return moment(date).isValid();
}
function getStartOfDay({ date, shouldUseUtc, } = {}) {
    return changeTimeOfDay({
        date,
        shouldUseUtc,
        partOfDay: EPointOfTimeUnit.start,
    });
}
function getEndOfDay({ date, shouldUseUtc, } = {}) {
    return changeTimeOfDay({
        date,
        shouldUseUtc,
        partOfDay: EPointOfTimeUnit.end,
    });
}
function changeTimeOfDay({ date, partOfDay, shouldUseUtc, }) {
    const momentDate = toMoment({ date, shouldUseUtc });
    if (partOfDay === EPointOfTimeUnit.start) {
        momentDate.startOf("day");
    }
    else {
        momentDate.endOf("day");
    }
    return momentDate.toDate();
}
function addDays({ date, days }) {
    return moment(date).add(days, "days").toDate();
}
function isValidDateFormat(date) {
    return moment(date, Object.values(EAllowedDateFormat), true).isValid();
}
function format({ date, format = EAllowedDateFormat.generalWithSpace, shouldUseUtc, }) {
    if (date === "") {
        return "";
    }
    return toMoment({ date, shouldUseUtc }).format(format);
}
function diffBetweenTwoDatesInDays(dateA, dateB) {
    return moment(dateA)
        .startOf("day")
        .diff(moment(dateB).startOf("day"), "days");
}
function sort(dates) {
    return dates.sort((a, b) => diffBetweenTwoDatesInDays(a, b));
}
function diffFromTodayInDays({ date, shouldIncludeToday = true, }) {
    const now = new Date();
    const diff = diffBetweenTwoDatesInDays(date, now);
    if (shouldIncludeToday) {
        return diff >= 0 ? diff + 1 : diff - 1;
    }
    return diff;
}
function isToday(date) {
    return moment().isSame(moment(date), "day");
}
function isSameDate({ dateA, dateB, timeUnit, }) {
    return moment(dateA).isSame(dateB, timeUnit);
}
function toMoment({ date, shouldUseUtc }) {
    return shouldUseUtc ? moment.utc(date) : moment(date);
}
function getPointOfTimeUnit({ timeUnit, pointOfTimeUnit, }) {
    let newDate;
    if (pointOfTimeUnit === EPointOfTimeUnit.start) {
        newDate = moment().startOf(timeUnit);
    }
    else {
        newDate = moment().endOf(timeUnit);
    }
    return newDate.toDate().getTime();
}

var dateTimeUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Duration: Duration,
  get EAllowedDateFormat () { return EAllowedDateFormat; },
  get EPointOfTimeUnit () { return EPointOfTimeUnit; },
  get ETimeUnit () { return ETimeUnit; },
  addDays: addDays,
  diffBetweenTwoDatesInDays: diffBetweenTwoDatesInDays,
  diffFromTodayInDays: diffFromTodayInDays,
  format: format,
  getEndOfDay: getEndOfDay,
  getPointOfTimeUnit: getPointOfTimeUnit,
  getStartOfDay: getStartOfDay,
  isSameDate: isSameDate,
  isToday: isToday,
  isValidDate: isValidDate,
  isValidDateFormat: isValidDateFormat,
  sort: sort
});

var EWebEditorMode;
(function (EWebEditorMode) {
    EWebEditorMode["editor"] = "editor";
    EWebEditorMode["player"] = "player";
})(EWebEditorMode || (EWebEditorMode = {}));
function generateWebEditorUrl({ baseUrl, projectId, mode = EWebEditorMode.player, }) {
    if (!baseUrl) {
        throw new Error("generateWebEditorUrl: baseUrl may not be empty");
    }
    if (!projectId) {
        throw new Error("generateWebEditorUrl: projectId may not be empty");
    }
    return `${baseUrl}/#${mode}/${projectId}`;
}

var urlGeneration = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get EWebEditorMode () { return EWebEditorMode; },
  generateWebEditorUrl: generateWebEditorUrl
});

const VPM_QUOTA_THRESHOLD = 0.9;
const GRACE_PERIOD = 21;
var EQuotaIdentifier;
(function (EQuotaIdentifier) {
    EQuotaIdentifier["waypoint"] = "waypoint";
    EQuotaIdentifier["timetravelOnWaypoint"] = "timetravel_on_waypoint";
    EQuotaIdentifier["projectArea"] = "project_area";
    EQuotaIdentifier["projectCount"] = "project_count";
    EQuotaIdentifier["userCount"] = "user_count";
})(EQuotaIdentifier || (EQuotaIdentifier = {}));
var EVpmLimitStatus;
(function (EVpmLimitStatus) {
    EVpmLimitStatus["limitAlmostReached"] = "limitAlmostReached";
    EVpmLimitStatus["limitReached"] = "limitReached";
    EVpmLimitStatus["limitExceeded"] = "limitExceeded";
    EVpmLimitStatus["projectFrozen"] = "projectFrozen";
})(EVpmLimitStatus || (EVpmLimitStatus = {}));
function getCurrentVpmLimitProperties(projectQuotas) {
    let currentLimitStatus = null;
    let deadlineInDaysFromNow = null;
    projectQuotas.forEach((quota) => {
        if (quota.current > quota.limit && quota.deadline) {
            const currentDeadlineInDaysFromNow = diffFromTodayInDays({
                date: quota.deadline,
                shouldIncludeToday: false,
            });
            const isDeadLinePassed = new Date() > new Date(quota.deadline);
            if (currentLimitStatus !== EVpmLimitStatus.projectFrozen) {
                currentLimitStatus = isDeadLinePassed
                    ? EVpmLimitStatus.projectFrozen
                    : EVpmLimitStatus.limitExceeded;
            }
            if (deadlineInDaysFromNow === null ||
                deadlineInDaysFromNow > currentDeadlineInDaysFromNow) {
                deadlineInDaysFromNow = currentDeadlineInDaysFromNow;
            }
        }
        else if (currentLimitStatus !== EVpmLimitStatus.projectFrozen &&
            currentLimitStatus !== EVpmLimitStatus.limitExceeded &&
            quota.limit !== 1) {
            if (quota.current === quota.limit) {
                currentLimitStatus = EVpmLimitStatus.limitReached;
            }
            else if (quota.current >= quota.limit * VPM_QUOTA_THRESHOLD) {
                currentLimitStatus = EVpmLimitStatus.limitAlmostReached;
            }
        }
    });
    if (currentLimitStatus === EVpmLimitStatus.projectFrozen) {
        deadlineInDaysFromNow = null;
    }
    return { currentLimitStatus, deadlineInDaysFromNow };
}
function generateVpmLimitBannerText(vpmLimitStatus, deadlineFromTodayInDays) {
    let remainingTimeToFreeze = `${deadlineFromTodayInDays} days`;
    switch (vpmLimitStatus) {
        case EVpmLimitStatus.projectFrozen:
            return `Your project has exceeded its plan limit for over ${GRACE_PERIOD} days. `;
        case EVpmLimitStatus.limitExceeded:
            if (deadlineFromTodayInDays === 0) {
                remainingTimeToFreeze = "few hours";
            }
            else if (deadlineFromTodayInDays === 1) {
                remainingTimeToFreeze = "1 day";
            }
            return `You have exceeded your plan limit. Project access will expire in ${remainingTimeToFreeze}. `;
        case EVpmLimitStatus.limitReached:
            return "You have reached your plan limit. ";
        case EVpmLimitStatus.limitAlmostReached:
            return "Heads up! You have reached 90% of your plan limit. ";
    }
}
function generateGuidanceText(vpmLimitStatus) {
    let guidanceText = "Please contact your HoloBuilder representative";
    if (vpmLimitStatus === EVpmLimitStatus.projectFrozen) {
        guidanceText += " to regain full project access.";
    }
    else {
        guidanceText += ".";
    }
    return guidanceText;
}

var vpmLimits = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get EQuotaIdentifier () { return EQuotaIdentifier; },
  get EVpmLimitStatus () { return EVpmLimitStatus; },
  GRACE_PERIOD: GRACE_PERIOD,
  VPM_QUOTA_THRESHOLD: VPM_QUOTA_THRESHOLD,
  generateGuidanceText: generateGuidanceText,
  generateVpmLimitBannerText: generateVpmLimitBannerText,
  getCurrentVpmLimitProperties: getCurrentVpmLimitProperties
});

const EMAIL_VALIDATION_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
function isValidEmail(email) {
    return EMAIL_VALIDATION_REGEX.test(email);
}
function containsEmail(text) {
    const textLength = text.length;
    for (let i = 0; i < textLength; i++) {
        for (let j = i + 1; j <= textLength; j++) {
            const substring = text.substring(i, j);
            if (isValidEmail(substring)) {
                return true;
            }
        }
    }
    return false;
}
function hasMinCharLength(text, minLength) {
    let length = minLength;
    if (minLength < 0) {
        length = 0;
    }
    return text.length >= length;
}
function hasUppercaseLetter(text) {
    return /[A-Z]/.test(text);
}
function hasLowercaseLetter(text) {
    return /[a-z]/.test(text);
}
function hasNumber(text) {
    return /[0-9]/.test(text);
}
function hasSpecialChar(text) {
    return /[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(text);
}
function hasNoLeadingOrTrailingSpace(text) {
    return text.trim() === text;
}

var stringUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  EMAIL_VALIDATION_REGEX: EMAIL_VALIDATION_REGEX,
  containsEmail: containsEmail,
  hasLowercaseLetter: hasLowercaseLetter,
  hasMinCharLength: hasMinCharLength,
  hasNoLeadingOrTrailingSpace: hasNoLeadingOrTrailingSpace,
  hasNumber: hasNumber,
  hasSpecialChar: hasSpecialChar,
  hasUppercaseLetter: hasUppercaseLetter,
  isValidEmail: isValidEmail
});

export { AmplitudeClient, Analytics, asyncUtils as AsyncUtils, browserUtils as BrowserUtils, CSVExporter, Convert, DataFormatter, dateTimeUtils as DateTimeUtils, downloadUtils as DownloadUtils, errorUtils as ErrorUtils, fileDownloadCallbacks as FileDownloadCallbacks, Localization, Logger, RepairUrl, runtimeUtils as RuntimeUtils, sanitizeUtils as SanitizeUtils, stringUtils as StringUtils, types as Types, urlGeneration as UrlGenerationUtils, vpmLimits as VpmLimits };
